import { Container } from "components/layout/Container";
import s from "./Filters.module.scss";
import {
  setClear,
  setFormats,
  setLevels,
  setModalOpen,
  setSubjects,
  setTypes,
} from "@/store/actions/samplesActions";

import { MultiSelect } from "components/pages/samples/MultiSelect";
import { useEffect, useState } from "react";
import cn from "classnames";
import { Button } from "components/UI/Buttons/Button";
import useWindowSize from "@/helpers/useWindowSize";
import { setBodyOverflow } from "store/slices/globalSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  sampleFilteredListSelector,
  sampleFiltersSelector,
  sampleSettingsSelector,
} from "store/slices/samplesSlice";
import { SamplesSearchInput } from "./SamplesSearchInput";
import iconFilter from "public/img/icons/svg/filter.svg";
import iconArrowLeft from "public/img/icons/svg/arrow-left.svg";
import iconClose from "public/img/icons/svg/times.svg";
import { SpriteIcon } from "@/components/UI/SpriteIcon";
import cs from "@/styles/modules/card.module.scss";
import { SamplesMultiSelect } from "@/components/pages/samples/SamplesMultiSelect";

export const Filters = () => {
  const dispatch = useAppDispatch();
  const { idAcademicDisciplines, idPaperTypes, idAcademicLevels, idCitationStyles } =
    useAppSelector(sampleFiltersSelector);
  const {
    idAcademicDisciplines: selectedSubjects,
    idPaperTypes: selectedTypes,
    idAcademicLevels: selectedLevels,
    idCitationStyles: selectedFormats,
    modalOpen,
  } = useAppSelector(sampleSettingsSelector);

  const { width } = useWindowSize();

  const isTablet = width <= 992 && width > 576;
  const isMobile = width <= 576;
  const isDesktop = width > 992;

  const dilteredlist = useAppSelector(sampleFilteredListSelector);

  const selectedFilters = {
    subjects: idAcademicDisciplines.filter(({ id }) => selectedSubjects.includes(id)),
    types: idPaperTypes.filter(({ id }) => selectedTypes.includes(id)),
    levels: idAcademicLevels.filter(({ id }) => selectedLevels.includes(id)),
    formats: idCitationStyles.filter(({ id }) => selectedFormats.includes(id)),
  };

  // console.log(modalOpen);

  const [isOpen, setIsOpen] = useState(false);
  const handleSubjectChange = (value: (number | string)[]) => {
    dispatch(setSubjects(value));
  };
  const handleTypeChange = (value: (number | string)[]) => {
    dispatch(setTypes(value));
  };
  const handleLevelChange = (value: (number | string)[]) => {
    dispatch(setLevels(value));
  };
  const handleFormatChange = (value: (number | string)[]) => {
    dispatch(setFormats(value));
  };

  const checkEmptyFilters = () => {
    const list = [selectedSubjects, selectedTypes, selectedLevels, selectedFormats].map(
      (list) => !list.length
    );
    return list.filter((item) => !item).length === 0;
  };

  const isListEmpty = checkEmptyFilters();

  const handleFilterOpen = () => {
    !isOpen && (isTablet || isMobile) && setIsOpen(true);
  };
  const handleFilterClose = () => {
    modalOpen && dispatch(setModalOpen(false));
    isOpen && !modalOpen && setIsOpen(false);
  };
  const showResults = () => {
    dispatch(setModalOpen(false));
    setIsOpen(false);
  };
  const delSubjectItem = (id: number | string) => {
    dispatch(setSubjects(selectedSubjects.filter((item) => item !== id)));
  };
  const delTypeItem = (id: number | string) => {
    dispatch(setTypes(selectedTypes.filter((item) => item !== id)));
  };
  const delLevelItem = (id: number | string) => {
    dispatch(setLevels(selectedLevels.filter((item) => item !== id)));
  };
  const delFormatItem = (id: number | string) => {
    dispatch(setFormats(selectedFormats.filter((item) => item !== id)));
  };
  const clearAll = () => {
    dispatch(setClear());
  };

  const { bodyOverflow } = useAppSelector((state) => state.global);

  useEffect(() => {
    isDesktop && setIsOpen(false);
    if (isOpen) dispatch(setBodyOverflow(true));
    else dispatch(setBodyOverflow(false));
  }, [isOpen, isDesktop, dispatch]);

  useEffect(() => {
    if (bodyOverflow) document.querySelector("body")?.classList.add("overflowHidden");
    else document.querySelector("body")?.classList.remove("overflowHidden");
  }, [bodyOverflow]);

  return (
    <div
      className={cn(s.wrapper, {
        [s.wrapperOpen]: isOpen,
      })}
    >
      <Container className={cn(cs.cardGlassHover, s.container, { [s.containerOpen]: isOpen })}>
        {isTablet && (
          <SamplesSearchInput
            className={cn(s.searchInput, {
              [s.searchInputOpen]: isOpen,
            })}
          />
        )}

        <div className={cn(s.overflow, { [s.overflowOpen]: isOpen })} onClick={handleFilterOpen}>
          <div className={cn(s.heading, { [s.headingOpen]: isOpen })}>
            {isOpen && (
              <div className={s.backBtn} onClick={handleFilterClose}>
                <SpriteIcon icon={iconArrowLeft} />
                Back
              </div>
            )}
            <span className={s.filterByText}>Filter by</span>
            {isOpen && (
              <div
                className={cn(s.clearBtn, {
                  [s.clearBtnDisabled]: isListEmpty,
                })}
                onClick={clearAll}
              >
                Clear all
              </div>
            )}
            <Button
              type="white"
              className={cn(s.filterMobileBtn, {
                [s.filterMobileBtnOpen]: isOpen,
              })}
              icon={iconFilter}
              iconPosition="suffix"
              size="fullwidth"
            >
              Filter by
            </Button>
          </div>
          <div
            className={cn(s.filters, {
              [s.filtersOpen]: isOpen,
            })}
          >
            <SamplesMultiSelect
              list={idAcademicDisciplines}
              stateList={selectedSubjects}
              setSelected={handleSubjectChange}
              placeholder="Subject"
              className={s.select}
            />
            <SamplesMultiSelect
              list={idPaperTypes}
              stateList={selectedTypes}
              setSelected={handleTypeChange}
              placeholder="Paper type"
              className={s.select}
            />
            <SamplesMultiSelect
              list={idAcademicLevels}
              stateList={selectedLevels}
              setSelected={handleLevelChange}
              placeholder="Level"
              className={s.select}
            />
            <SamplesMultiSelect
              list={idCitationStyles}
              stateList={selectedFormats}
              setSelected={handleFormatChange}
              placeholder="Format"
              className={s.select}
            />
          </div>
          {isOpen && (
            <div className={s.stateList}>
              {selectedFilters.subjects.length > 0 && (
                <div className={s.stateListFilter}>
                  <div className={s.stateListTitle}>Subject</div>
                  <div className={s.stateListItems}>
                    {selectedFilters.subjects.map(({ id, customerName }) => (
                      <div key={id} className={s.stateListItem}>
                        {customerName}
                        <SpriteIcon icon={iconClose} onClick={() => delSubjectItem(id)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Вынести в компонент */}
              {selectedFilters.types.length > 0 && (
                <div className={s.stateListFilter}>
                  <div className={s.stateListTitle}>Paper type</div>
                  <div className={s.stateListItems}>
                    {selectedFilters.types.map(({ id, customerName }) => (
                      <div key={id} className={s.stateListItem}>
                        {customerName}
                        <SpriteIcon icon={iconClose} onClick={() => delTypeItem(id)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedFilters.levels.length > 0 && (
                <div className={s.stateListFilter}>
                  <div className={s.stateListTitle}>Level</div>
                  <div className={s.stateListItems}>
                    {selectedFilters.levels.map(({ id, customerName }) => (
                      <div key={id} className={s.stateListItem}>
                        {customerName}
                        <SpriteIcon icon={iconClose} onClick={() => delLevelItem(id)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedFilters.formats.length > 0 && (
                <div className={s.stateListFilter}>
                  <div className={s.stateListTitle}>Format</div>
                  <div className={s.stateListItems}>
                    {selectedFilters.formats.map(({ id, customerName }) => (
                      <div key={id} className={s.stateListItem}>
                        {customerName}
                        <SpriteIcon icon={iconClose} onClick={() => delFormatItem(id)} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {isOpen && (
          <div className={s.buttonWrapper}>
            <Button className={s.button} type="primary" outline size="fullwidth" onClick={showResults}>
              Show results ({dilteredlist.length})
            </Button>
          </div>
        )}
      </Container>
    </div>
  );
};
